import { safeParseNullDefault, safeParse } from 'shouldve-been-js';

class DocumentTreeNode {

    private _nodeId: number;
    private _parentId: number | null;
    private _name: string;
    private _sortNumber: number;
    private _hasAccess: boolean;

    public constructor(nodeId: number, parentId: number | null, name: string, sortNumber: number, hasAccess: boolean) {

        this._nodeId = nodeId;
        this._parentId = parentId;
        this._name = name;
        this._sortNumber = sortNumber;
        this._hasAccess = hasAccess;
    }

    public get nodeId(): number {
        return this._nodeId;
    }

    public set nodeId(value: number) {
        this._nodeId = value;
    }

    public get parentId(): number | null {
        return this._parentId;
    }

    public set parentId(value: number | null) {
        this._parentId = value;
    }

    public get name(): string {
        return this._name;
    }

    public set name(value: string) {
        this._name = value;
    }

    public get sortNumber(): number {
        return this._sortNumber;
    }

    public set sortNumber(value: number) {
        this._sortNumber = value;
    }

    public get hasAccess(): boolean {
        return this._hasAccess;
    }

    public set hasAccess(value: boolean) {
        this._hasAccess = value;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    public static fromJson(json: any): DocumentTreeNode | null {

        const nodeId: number | null = safeParseNullDefault<number>(json?.nodeId, 0);
        let parentId: number | null = safeParseNullDefault<number>(json?.parentId, 0);
        const name: string = safeParse<string>(json?.name, '');
        const sortNumber: number | null = safeParseNullDefault<number>(json?.sortNumber, 0);
        const hasAccess: boolean = safeParse<boolean>(json?.hasAccess, false);
        
        if (nodeId === null || sortNumber === null) {
            return null;
        }

        if (parentId !== null && parentId < 0) {
            parentId = null;
        }

        return new DocumentTreeNode(nodeId, parentId, name, sortNumber, hasAccess);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    public static fromIndexedDB(object: any): DocumentTreeNode | null {

        const nodeId: number | null = safeParseNullDefault<number>(object?._nodeId, 0);
        const parentId: number | null = safeParseNullDefault<number>(object?._parentId, 0);
        const name: string = safeParse<string>(object?._name, '');
        const sortNumber: number | null = safeParseNullDefault<number>(object?._sortNumber, 0);
        const hasAccess: boolean = safeParse<boolean>(object?._hasAccess, false);
        
        if (nodeId === null || sortNumber === null) {
            return null;
        }

        return new DocumentTreeNode(nodeId, parentId, name, sortNumber, hasAccess);
    }

    public static filterAvailableNodes(allNodes: DocumentTreeNode[]): DocumentTreeNode[] {

        return allNodes.filter((node: DocumentTreeNode) => { //could improve performance if needed as a lot of nodes are reevaluated for each iteration of this loop

            return this.nodeIsAvailable(node, allNodes, [node.nodeId]);
        });
    }

    protected static nodeIsAvailable(node: DocumentTreeNode, allNodes: DocumentTreeNode[], alreadyCheckedNodeIds: number[]): boolean {

        if (node.hasAccess) {
            return true;
        }

        //the alreadyCheckedNodeIds prevents infinite loops in case some nodes are misconfigured to create a loop
        const childNodes: DocumentTreeNode[] = allNodes.filter((childNode: DocumentTreeNode) => childNode.parentId === node.nodeId && !alreadyCheckedNodeIds.includes(childNode.nodeId));

        return childNodes.some((childNode: DocumentTreeNode) => this.nodeIsAvailable(childNode, allNodes, alreadyCheckedNodeIds.concat([childNode.nodeId])));
    }
}

export default DocumentTreeNode;